'use client';
import { useAppStore } from '@/stores/app-store';
import { DEV_TYPES, FIELD_META } from '@/lib/device-types';
import { getAIInsights, siteScore } from '@/lib/ai-engine';
import { fmt, isDarkTheme } from '@/lib/helpers';
import { t } from '@/lib/i18n';
import { DashWidget, Plant, Device } from '@/lib/types';
import { useDeviceLatest } from '@/lib/use-device-latest';
import { NO_VALUE, isCumulativeField } from '@/lib/device-status';
import FlowGraph from './FlowGraph';
import ApexChartWrapper from './ApexChartWrapper';
import LineChartWidget from './widgets/LineChartWidget';
import PeriodCompareChartWidget from './widgets/PeriodCompareChartWidget';
import BarChartWidget from './widgets/BarChartWidget';
import EnergyUsageTableWidget from './widgets/EnergyUsageTableWidget';
import StatusWidget from './widgets/StatusWidget';
import GaugeWidget from './widgets/GaugeWidget';
import AlarmSettingWidget from './widgets/AlarmSettingWidget';
import ImageWidget from './widgets/ImageWidget';
import TimeWidget from './widgets/TimeWidget';
import MapWidget from './widgets/MapWidget';
import KhonBuriWaterWidget from './widgets/KhonBuriWaterWidget';
import ScadaWidget from './widgets/ScadaWidget';
import LevelPumpChart from './widgets/LevelPumpChart';
import WwtpScadaWidget from './widgets/WwtpScadaWidget';
import MtechWwtpWidget from './widgets/MtechWwtpWidget';
import MtechWwtpSummary from './widgets/MtechWwtpSummary';
import ChillerScadaWidget from './widgets/ChillerScadaWidget';
import ImagingSuiteWidget from './widgets/ImagingSuiteWidget';
import ChillerDemoWidget from './widgets/ChillerDemoWidget';
import AirSystemScadaWidget from './widgets/AirSystemScadaWidget';
import AirCompressorWidget from './widgets/AirCompressorWidget';
import WaterSystemScadaWidget from './widgets/WaterSystemScadaWidget';
import ZoneEnergyWidget from './widgets/ZoneEnergyWidget';
import ChwFlowWidget from './widgets/ChwFlowWidget';
import WaterQualityScadaWidget from './widgets/WaterQualityScadaWidget';
import MdbScadaWidget from './widgets/MdbScadaWidget';
import EnergyFlowScadaWidget from './widgets/EnergyFlowScadaWidget';
import EnergyHubScadaWidget from './widgets/EnergyHubScadaWidget';
import EvStationWidget from './widgets/EvStationWidget';
import HumidityTrialWidget from './widgets/HumidityTrialWidget';
import ColdRoomWidget from './widgets/ColdRoomWidget';
import BkcWidget from './widgets/BkcWidget';
import SahaFarmWidget from './widgets/SahaFarmWidget';
import SwineFarmWidget from './widgets/SwineFarmWidget';
import SchoolWidget from './widgets/SchoolWidget';
import MegaMallWidget from './widgets/MegaMallWidget';
import SisbWidget from './widgets/SisbWidget';
import FloodWidget from './widgets/FloodWidget';
import SiteEnergyMapWidget from './widgets/SiteEnergyMapWidget';
import ResortOpsWidget from './widgets/ResortOpsWidget';
import OrMonitorWidget from './widgets/OrMonitorWidget';
import GensetMonitorWidget from './widgets/GensetMonitorWidget';
import PowerQualityWidget from './widgets/PowerQualityWidget';
import MedicalScadaWidget from './widgets/MedicalScadaWidget';
import QcMonitorWidget from './widgets/QcMonitorWidget';
import GrinderScadaWidget from './widgets/GrinderScadaWidget';
import ExtruderScadaWidget from './widgets/ExtruderScadaWidget';
import PowerScadaWidget from './widgets/PowerScadaWidget';
import UtilityScadaWidget from './widgets/UtilityScadaWidget';
import ScadaTabsWidget from './widgets/ScadaTabsWidget';
import FacilityMonitorWidget from './widgets/FacilityMonitorWidget';
import FacilityTwinWidget from './widgets/FacilityTwinWidget';
import Wwtp3DWidget from './widgets/Wwtp3DWidget';
import AqDisplayWidget from './widgets/AqDisplayWidget';
import AirportStandsWidget from './widgets/AirportStandsWidget';
import FactoryScadaWidget from './widgets/FactoryScadaWidget';
import FoodFactoryScadaWidget from './widgets/FoodFactoryScadaWidget';
import BoilerScadaWidget from './widgets/BoilerScadaWidget';
import SteamScadaWidget from './widgets/SteamScadaWidget';
import CoolingScadaWidget from './widgets/CoolingScadaWidget';
import WaterScadaWidget from './widgets/WaterScadaWidget';
import ZonesFactoryScadaWidget from './widgets/ZonesFactoryScadaWidget';
import ChillerLoopScadaWidget from './widgets/ChillerLoopScadaWidget';
import ProcessLineScadaWidget from './widgets/ProcessLineScadaWidget';
import FactoryDemoWidget from './widgets/FactoryDemoWidget';
import MeterGridScadaWidget from './widgets/MeterGridScadaWidget';
import GroupEnergyReportWidget from './widgets/GroupEnergyReportWidget';
import PlantUnifiedWidget from './widgets/PlantUnifiedWidget';
import PlantTwinWidget from './widgets/PlantTwinWidget';
import PoultryHouseScadaWidget from './widgets/PoultryHouseScadaWidget';
import TankScadaWidget from './widgets/TankScadaWidget';
import DeviceConnectivityWidget from './widgets/DeviceConnectivityWidget';
import HvacScadaWidget from './widgets/HvacScadaWidget';
import HotelScadaWidget from './widgets/HotelScadaWidget';
import MallScadaWidget from './widgets/MallScadaWidget';
import RetailPortfolioWidget from './widgets/RetailPortfolioWidget';
import PhotoScadaWidget from './widgets/PhotoScadaWidget';
import MeterCompareWidget from './widgets/MeterCompareWidget';
import SolarAssessWidget from './widgets/SolarAssessWidget';
import LoadProfileWidget from './widgets/LoadProfileWidget';
import MdbHealthWidget from './widgets/MdbHealthWidget';
import KSaverWidget from './widgets/KSaverWidget';
import EnergySavingWidget from './widgets/EnergySavingWidget';
import SolarFlowWidget from './widgets/SolarFlowWidget';
import SolarPlantWidget from './widgets/SolarPlantWidget';
import SolarBalanceWidget from './widgets/SolarBalanceWidget';

// Widget types whose value comes from device.data snapshot — these benefit
// from background polling via /api/devices/[macId]/latest so the badge /
// number on screen tracks the device without a page reload.
const LIVE_REFRESH_TYPES = new Set([
  'single_card', 'card', 'gauge', 'status', 'donut_chart', 'scada', 'tank_scada', 'ksaver', 'solar_flow', 'solar_balance', 'mdb_health',
]);

const THEME_COLORS: Record<string, string> = {
  amber: 'var(--amber)',
  blue: 'var(--blue)',
  green: 'var(--green)',
  red: 'var(--red)',
  cyan: 'var(--cyan)',
  purple: 'var(--purple)',
};

function getColor(c: string): string {
  return THEME_COLORS[c] || 'var(--text-primary)';
}

// A device's live reading for `key`, or undefined while it is offline (the
// last reading is stale — see lib/device-status.ts). Cumulative counters stay.
function liveValue(dev: Device | undefined, key: string): number | undefined {
  if (!dev) return undefined;
  if (dev.st === 'offline' && !isCumulativeField(key, dev.units?.[key])) return undefined;
  return dev.data?.[key];
}

// Pick an icon that matches the metric a value card is showing.
export function fieldIcon(field: string, color: string, size = 22): React.ReactNode {
  const f = (field || '').toLowerCase();
  const p = {
    width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: color,
    strokeWidth: 1.7, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
  };
  const bolt = <svg {...p} fill={color} stroke="none"><path d="M13 2L4 14h7l-1 8 10-12h-7z" /></svg>;
  if (f.includes('temp')) return <svg {...p}><path d="M10 13.5V5a2 2 0 1 1 4 0v8.5a4 4 0 1 1-4 0z" /><line x1="12" y1="8.5" x2="12" y2="13.5" /></svg>;
  if (f === 'kw' || f.includes('power')) return bolt;
  if (f.includes('kwh') || f.includes('energy') || f.includes('daily')) return <svg {...p}><rect x="2.5" y="7" width="15" height="10" rx="2" /><line x1="20.5" y1="10.5" x2="20.5" y2="13.5" /><path d="M10 9.5l-2 3h3l-2 3" /></svg>;
  if (f.includes('humid') || f.includes('water')) return <svg {...p}><path d="M12 3s6 6.5 6 10a6 6 0 0 1-12 0c0-3.5 6-10 6-10z" /></svg>;
  if (f.includes('volt') || /^v[abc]?$/.test(f)) return bolt;
  if (f.includes('current') || f.includes('load') || f.includes('amp')) return <svg {...p}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>;
  if (f.includes('pf') || f.includes('eff')) return <svg {...p}><path d="M4 14a8 8 0 0 1 16 0" /><line x1="12" y1="14" x2="15.5" y2="10" /></svg>;
  if (f.includes('freq')) return <svg {...p}><path d="M2 12c2.5-5 4.5-5 7 0s4.5 5 7 0" /></svg>;
  if (f.includes('pm') || f.includes('co2') || f.includes('noise') || f.includes('air')) return <svg {...p}><path d="M3 12h11a3 3 0 1 0-3-3" /><path d="M3 16h7a2.5 2.5 0 1 1-2.5 2.5" /></svg>;
  return <svg {...p}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>;
}

// Elapsed-since-last-reading as H:MM:SS, e.g. "805:27:37 hrs. ago".
function formatAgoHms(ts: string | null | undefined): string | null {
  if (!ts) return null;
  const d = new Date(ts);
  if (isNaN(d.getTime())) return null;
  let s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 0) s = 0;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${Math.floor(s / 3600)}:${p(Math.floor((s % 3600) / 60))}:${p(s % 60)} hrs. ago`;
}

const FIELD_ICON_TYPES = new Set(['single_card', 'gauge']);
// Header icon matching the metric, for single-field value widgets.
// Returns null so the caller can fall back to the per-widget-type icon.
export function widgetHeadIcon(
  widget: { type: string; field?: string; seriesColors?: Record<string, string> }
): { icon: React.ReactNode; color: string } | null {
  if (!FIELD_ICON_TYPES.has(widget.type)) return null;
  const field = widget.field || '';
  if (!field || field.includes(',')) return null;
  const fm = FIELD_META[field] || { color: 'blue' };
  const color = getColor(widget.seriesColors?.[field] ?? fm.color);
  return { icon: fieldIcon(field, color, 13), color };
}

// ── Inline SVG icons for device categories ──
function DeviceIcon({ cat }: { cat: string }) {
  const size = 14;
  const color = cat === 'solar' ? 'var(--amber)' : cat === 'energy' ? 'var(--blue)' : 'var(--green)';
  if (cat === 'solar') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="14" height="12" rx="1.5" />
        <line x1="8" y1="3" x2="8" y2="15" />
        <line x1="13" y1="3" x2="13" y2="15" />
        <line x1="3" y1="9" x2="17" y2="9" />
        <path d="M7 15v3.5M13 15v3.5" />
        <path d="M5 18.5h5M11 18.5h5" />
        <circle cx="20" cy="5" r="2" />
        <path d="M20 1v2M20 7v2M17 5h1M21 5h2" />
      </svg>
    );
  }
  if (cat === 'energy') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M13 2L4 14h7l-1 8 10-12h-7l1-8z" />
      </svg>
    );
  }
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 8C8 10 5.9 16.17 3.82 21.34l1.89.66L7 18" />
      <path d="M17 8c2-2 4-4 4-8-4 0-6 2-8 4" />
      <path d="M17 8l-4 4" />
    </svg>
  );
}

// ── Shared styles ──
const noDataStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  color: 'var(--text-muted)',
  fontSize: 13,
};

const labelStyle: React.CSSProperties = {
  fontSize: 9,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  fontWeight: 700,
};

const unitStyle: React.CSSProperties = {
  fontSize: 9,
  color: 'var(--text-muted)',
};

const statBoxStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 60,
  textAlign: 'center',
  padding: 8,
  borderRadius: 8,
  background: 'var(--bg-elevated)',
};

interface WidgetRendererProps {
  widget: DashWidget;
  plant: Plant;
}

export default function WidgetRenderer({ widget, plant }: WidgetRendererProps) {
  const lang = useAppStore((s) => s.lang);
  const dbUser = useAppStore((s) => s.dbUser);

  // Subscribe to the live snapshot for this widget's primary device. The
  // hook is ref-counted so N widgets pointing at the same device share one
  // polling cycle; chart widgets and non-snapshot widgets pass enabled=false
  // so they don't trigger background traffic they wouldn't consume.
  const needsLive = !!dbUser && LIVE_REFRESH_TYPES.has(widget.type);
  const live = useDeviceLatest(needsLive ? widget.deviceSN : null, needsLive);

  const findDevice = (sn: string): Device | undefined => {
    const base = plant.devices.find((d) => d.sn === sn);
    if (!base) return undefined;
    if (!live || live.macId !== sn) return base;
    // Merge fresh values over the snapshot. Only fields present in `live.data`
    // are overwritten — others (e.g. derived `daily` from energy_usage_*)
    // keep their snapshot values. A null value means the device is offline
    // now: drop the (possibly stale, from-login) snapshot value too.
    const data = { ...base.data };
    const dataByNo = base.dataByNo ? { ...base.dataByNo } : undefined;
    const liveUnits: Record<string, string> = {};
    for (const [k, v] of Object.entries(live.data)) {
      if (v.value === null) {
        delete data[k];
        if (dataByNo) delete dataByNo[v.fieldNo];
      } else {
        data[k] = v.value;
        if (dataByNo) dataByNo[v.fieldNo] = v.value;
      }
      if (v.unit) liveUnits[k] = v.unit;
    }
    return {
      ...base,
      data,
      dataByNo,
      units: { ...(base.units || {}), ...liveUnits },
      st: live.online ? 'online' : 'offline',
    };
  };

  switch (widget.type) {
    // ── Single Card ──
    // Original: center aligned, padding 10, value 36px/800, unit 11px text-muted
    case 'single_card': {
      const dev = findDevice(widget.deviceSN);
      if (!dev) return <div style={noDataStyle}>No device</div>;
      const val = liveValue(dev, widget.field);
      // Offline → the card stays, showing "—" instead of the stale reading.
      if (val === undefined && dev.st !== 'offline') return <div style={noDataStyle}>No data</div>;
      const fm = FIELD_META[widget.field] || { label: widget.field, unit: '', color: 'blue' };
      const unit = dev.units?.[widget.field] || fm.unit;
      const valueColor = widget.seriesColors?.[widget.field] ?? fm.color;
      const sc = getColor(valueColor);
      const scLabel = dev.fieldNames?.[widget.field] || FIELD_META[widget.field]?.label || widget.title || widget.field;
      const scAgo = formatAgoHms(live?.timestamp);
      return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 12, padding: '4px 8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 56, height: 56, borderRadius: 16, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: `color-mix(in srgb, ${sc} 14%, transparent)`,
              border: `1px solid color-mix(in srgb, ${sc} 30%, transparent)`,
            }}>
              {fieldIcon(widget.field, sc, 26)}
            </div>
            <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {scLabel}
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
                <span style={{ fontSize: 30, fontWeight: 800, color: sc, lineHeight: 1, letterSpacing: '-0.02em' }}>
                  {typeof val === 'number' ? val.toFixed(val < 10 ? 2 : 1) : (val ?? NO_VALUE)}
                </span>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)' }}>{unit}</span>
              </div>
            </div>
          </div>
          {scAgo && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{scAgo}</div>
          )}
        </div>
      );
    }

    // ── Multi-field Card ──
    // Original: flex wrap gap 6, each box flex 1 minWidth 60, padding 8, borderRadius 8, bg-elevated
    // Label 9px uppercase, value 16px/800, unit 9px
    case 'card': {
      const dev = findDevice(widget.deviceSN);
      if (!dev) return <div style={noDataStyle}>No device</div>;
      const fields = widget.field.split(',').map((f) => f.trim());
      return (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {fields.map((f) => {
            const fm = FIELD_META[f] || { label: f, unit: '', color: 'blue' };
            const val = liveValue(dev, f);
            const unit = dev.units?.[f] || fm.unit;
            const valueColor = widget.seriesColors?.[f] ?? fm.color;
            return (
              <div key={f} style={{
                flex: 1,
                minWidth: 60,
                textAlign: 'center',
                padding: 8,
                borderRadius: 8,
                background: 'var(--bg-elevated)',
              }}>
                <div style={{ ...labelStyle, marginBottom: 2 }}>{fm.label}</div>
                <div style={{
                  fontSize: 16,
                  fontWeight: 800,
                  color: getColor(valueColor),
                }}>
                  {val !== undefined ? (typeof val === 'number' ? val.toFixed(val < 10 ? 2 : 1) : val) : NO_VALUE}
                </div>
                <div style={unitStyle}>{unit}</div>
              </div>
            );
          })}
        </div>
      );
    }

    // ── Status ── (extracted to ./widgets/StatusWidget.tsx)
    case 'status':
      return <StatusWidget widget={widget} device={findDevice(widget.deviceSN)} />;

    // ── Gauge (responsive radialBar — extracted to ./widgets/GaugeWidget.tsx) ──
    case 'gauge':
      return <GaugeWidget widget={widget} device={findDevice(widget.deviceSN)} />;

    // ── Device List ──
    // Original: each row flex between, padding 5px 0, borderBottom border-subtle
    // Left: badge ON/OFF + icon + name (11px), right: S/N 9px text-muted
    case 'device_list': {
      const devs = plant.devices;
      if (!devs || devs.length === 0) return <div style={noDataStyle}>{t('noDevices', lang)}</div>;
      return (
        <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '100%', overflowY: 'auto' }}>
          {devs.map((d) => {
            const dt = DEV_TYPES[d.type];
            const isOn = d.st === 'online';
            const cat = dt?.cat || 'environment';
            return (
              <div key={d.sn} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '5px 0',
                borderBottom: '1px solid var(--border-subtle)',
              }}>
                <span style={{
                  fontSize: 11,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}>
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '2px 8px',
                    borderRadius: 99,
                    fontSize: 10,
                    fontWeight: 600,
                    background: isOn ? 'var(--green-dim)' : 'var(--red-dim)',
                    color: isOn ? 'var(--green)' : 'var(--red)',
                  }}>
                    <span style={{
                      width: 5,
                      height: 5,
                      borderRadius: '50%',
                      background: isOn ? 'var(--green)' : 'var(--red)',
                      boxShadow: isOn ? '0 0 6px var(--green)' : '0 0 6px var(--red)',
                      display: 'inline-block',
                    }} />
                    {isOn ? 'ON' : 'OFF'}
                  </span>
                  <DeviceIcon cat={cat} />
                  <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{d.name}</span>
                </span>
                <span style={{ fontSize: 9, color: 'var(--text-muted)' }}>{d.sn}</span>
              </div>
            );
          })}
        </div>
      );
    }

    // ── Line Chart ──
    case 'line_chart':
      return <LineChartWidget widget={widget} device={findDevice(widget.deviceSN)} plant={plant} />;

    // ── Bar Chart ──
    case 'bar_chart':
      return <BarChartWidget widget={widget} device={findDevice(widget.deviceSN)} />;

    // ── Period Compare Chart ──
    case 'period_compare_chart':
      return <PeriodCompareChartWidget widget={widget} device={findDevice(widget.deviceSN)} />;

    // ── Energy Usage Table ──
    case 'energy_usage_table':
      return <EnergyUsageTableWidget widget={widget} device={findDevice(widget.deviceSN)} plant={plant} />;

    // ── Flow Graph ──
    case 'flow_graph': {
      return <FlowGraph widget={widget} plant={plant} />;
    }

    // ── Accumulative Card ──
    // Original: 3 flex boxes gap 8, each flex 1 minWidth 80 padding 10 borderRadius 10 bg-elevated
    // Production green, Today amber, Import red, label 9px uppercase, value 18px/800, unit 9px
    case 'accum_card': {
      const devs = plant.devices;
      const totalKWH = devs.reduce((s, d) => s + (d.data?.kwh || 0), 0);
      const solarDevs = devs.filter((d) => DEV_TYPES[d.type]?.cat === 'solar');
      const totalDaily = solarDevs.reduce((s, d) => s + (d.data?.daily || 0), 0);
      const totalImport = devs.reduce((s, d) => s + (d.data?.import || 0), 0);
      const items = [
        { label: 'Production', value: fmt(totalKWH), unit: 'kWh', color: 'green' },
        { label: t('today', lang), value: totalDaily.toFixed(0), unit: 'kWh', color: 'amber' },
        { label: 'Import', value: fmt(totalImport), unit: 'kWh', color: 'red' },
      ];
      return (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {items.map((item) => (
            <div key={item.label} style={{
              flex: 1,
              minWidth: 80,
              padding: 10,
              borderRadius: 10,
              background: 'var(--bg-elevated)',
              textAlign: 'center',
            }}>
              <div style={labelStyle}>{item.label}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: getColor(item.color) }}>{item.value}</div>
              <div style={unitStyle}>{item.unit}</div>
            </div>
          ))}
        </div>
      );
    }

    // ── Donut Chart ──
    case 'donut_chart': {
      const dark = isDarkTheme();
      const fields = widget.field.split(',').map((f) => f.trim());
      const dev = findDevice(widget.deviceSN);
      const labels: string[] = [];
      const values: number[] = [];
      const colors: string[] = [];
      fields.forEach((f) => {
        const fm = FIELD_META[f] || { label: f, unit: '', color: 'blue' };
        const val = liveValue(dev, f);
        if (val !== undefined && val > 0) {
          labels.push(fm.label);
          values.push(+val.toFixed(2));
          colors.push(getColor(widget.seriesColors?.[f] ?? fm.color));
        }
      });
      if (values.length <= 1) {
        // Device bound but no live reading (offline / missing) → "—", not a
        // made-up 50% ring. The 50 placeholder is only for an unbound widget.
        if (dev && liveValue(dev, fields[0]) === undefined) return <div style={noDataStyle}>{NO_VALUE}</div>;
        const baseVal = liveValue(dev, fields[0]) ?? 50;
        const used = baseVal;
        const remaining = Math.max(0, 100 - baseVal);
        labels.length = 0; values.length = 0; colors.length = 0;
        labels.push('Used', 'Remaining');
        values.push(+used.toFixed(2), +remaining.toFixed(2));
        const fm = FIELD_META[fields[0]] || { color: 'blue' };
        const primary = widget.seriesColors?.[fields[0]] ?? fm.color;
        colors.push(getColor(primary), dark ? '#334155' : '#e2e8f0');
      }
      const options: ApexCharts.ApexOptions = {
        chart: { type: 'donut', background: 'transparent' },
        colors,
        labels,
        dataLabels: { enabled: false },
        legend: {
          position: 'bottom',
          fontSize: '10px',
          labels: { colors: dark ? '#94a3b8' : '#64748b' },
          markers: { size: 6, offsetX: -2 },
          itemMargin: { horizontal: 6, vertical: 2 },
        },
        plotOptions: {
          pie: {
            donut: {
              size: '65%',
              labels: {
                show: true,
                name: { show: true, fontSize: '10px', color: dark ? '#94a3b8' : '#64748b' },
                value: { show: true, fontSize: '16px', fontWeight: '700', color: dark ? '#e2e8f0' : '#1e293b' },
                total: { show: true, fontSize: '10px', color: dark ? '#94a3b8' : '#64748b' },
              },
            },
          },
        },
        stroke: { show: false },
        tooltip: { theme: dark ? 'dark' : 'light' },
      };
      return (
        <div style={{ width: '100%', height: '100%', padding: '4px' }}>
          <ApexChartWrapper options={options} series={values} type="donut" height="100%" />
        </div>
      );
    }

    // ── Solar Overview ──
    // Original: big power 32px/800 amber with "kW" span 13px, sub "Real-time Power" 10px text-muted
    // 2 stat boxes below: Today kWh green, Inverters count blue, flex gap 6, bg-elevated borderRadius 8
    case 'solar_overview': {
      const solarDevs = plant.devices.filter((d) => DEV_TYPES[d.type]?.cat === 'solar');
      // Live kW of online inverters only; "—" when none is reporting.
      const powerDevs = solarDevs.filter((d) => liveValue(d, 'kw') !== undefined);
      const totalPower = powerDevs.length > 0 ? powerDevs.reduce((s, d) => s + (liveValue(d, 'kw') as number), 0) : null;
      const totalDaily = solarDevs.reduce((s, d) => s + (d.data?.daily || 0), 0);
      const invCount = solarDevs.length;
      return (
        <div>
          <div style={{
            fontSize: 32,
            fontWeight: 800,
            color: getColor('amber'),
            lineHeight: 1.1,
          }}>
            {totalPower !== null ? totalPower.toFixed(1) : NO_VALUE} <span style={{ fontSize: 13 }}>kW</span>
          </div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 10 }}>
            Real-time Power
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <div style={{ ...statBoxStyle }}>
              <div style={labelStyle}>{t('today', lang)}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: getColor('green') }}>
                {totalDaily.toFixed(0)} kWh
              </div>
            </div>
            <div style={{ ...statBoxStyle }}>
              <div style={labelStyle}>Inverters</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: getColor('blue') }}>
                {invCount}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // ── Energy Balance ──
    // Original: 2 boxes top (Import red-dim bg, Export green-dim bg), net result below bg-elevated
    case 'energy_balance': {
      const energyDevs = plant.devices.filter((d) => d.data && (d.data.import || d.data.export));
      const totalImport = energyDevs.reduce((s, d) => s + (d.data?.import || 0), 0);
      const totalExport = energyDevs.reduce((s, d) => s + (d.data?.export || 0), 0);
      const netExporting = totalExport > totalImport;
      const netColor = netExporting ? 'green' : 'red';
      const netArrow = netExporting ? '\u2191' : '\u2193';
      return (
        <div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            <div style={{
              flex: 1,
              textAlign: 'center',
              padding: 8,
              borderRadius: 8,
              background: 'var(--red-dim)',
            }}>
              <div style={labelStyle}>Import</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: getColor('red') }}>{fmt(totalImport)}</div>
            </div>
            <div style={{
              flex: 1,
              textAlign: 'center',
              padding: 8,
              borderRadius: 8,
              background: 'var(--green-dim)',
            }}>
              <div style={labelStyle}>Export</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: getColor('green') }}>{fmt(totalExport)}</div>
            </div>
          </div>
          <div style={{
            textAlign: 'center',
            padding: 6,
            borderRadius: 8,
            background: 'var(--bg-elevated)',
          }}>
            <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Net: </span>
            <span style={{ fontSize: 14, fontWeight: 800, color: getColor(netColor) }}>
              {fmt(Math.abs(totalExport - totalImport))} kWh {netArrow}
            </span>
          </div>
        </div>
      );
    }

    // ── Environment Overview ──
    // Original: big avgT 28px/800 green with "°C" span 13px, sub "Avg Temperature" 10px
    // 2 stat boxes: Stations count blue, Online count green
    case 'env_overview': {
      const envDevs = plant.devices.filter((d) => DEV_TYPES[d.type]?.cat === 'environment' && d.st === 'online');
      const tempsWithData = envDevs.filter((d) => d.data?.temp !== undefined);
      const avgTemp = tempsWithData.length > 0
        ? tempsWithData.reduce((s, d) => s + (d.data?.temp || 0), 0) / tempsWithData.length
        : null;
      return (
        <div>
          <div style={{
            fontSize: 28,
            fontWeight: 800,
            color: getColor('green'),
            lineHeight: 1.1,
          }}>
            {avgTemp !== null ? avgTemp.toFixed(1) : NO_VALUE}
            <span style={{ fontSize: 13 }}>{'\u00b0'}C</span>
          </div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 10 }}>
            Avg Temperature
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <div style={{ ...statBoxStyle }}>
              <div style={labelStyle}>Stations</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: getColor('blue') }}>
                {envDevs.length}
              </div>
            </div>
            <div style={{ ...statBoxStyle }}>
              <div style={labelStyle}>{t('online', lang)}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: getColor('green') }}>
                {envDevs.length}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // ── Air Quality ──
    // Original: max PM2.5 32px/800 colored, quality label + per-sensor mini cards bg-elevated
    case 'air_quality': {
      const aqDevs = plant.devices.filter((d) => d.data?.pm25 !== undefined && d.st === 'online');
      if (aqDevs.length === 0) return <div style={noDataStyle}>No AQ sensor</div>;
      const pm25Vals = aqDevs.map((d) => d.data!.pm25 as number);
      const maxPM25 = Math.max(...pm25Vals);
      const pmColor = maxPM25 <= 25 ? 'green' : maxPM25 <= 37 ? 'amber' : 'red';
      const quality = maxPM25 <= 25 ? 'Good' : maxPM25 <= 37 ? 'Moderate' : 'Unhealthy';
      return (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <div style={{ fontSize: 32, fontWeight: 800, color: getColor(pmColor) }}>
              {maxPM25}
            </div>
            <div>
              <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>PM2.5 Max</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: getColor(pmColor) }}>{quality}</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {aqDevs.slice(0, 3).map((d) => (
              <div key={d.sn} style={{
                flex: 1,
                padding: 6,
                borderRadius: 6,
                background: 'var(--bg-elevated)',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: 7, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {d.name}
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>
                  {d.data!.pm25}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // ── Alert Summary ──
    // Original: total big 28px/800 colored, sub text, critical badge if any
    case 'alert_summary': {
      const insights = getAIInsights(plant);
      const warnCount = insights.warnings.length;
      const anomCount = insights.anomalies.length;
      const critCount = insights.warnings.filter((w) => w.sv === 'critical').length;
      const total = warnCount + anomCount;
      return (
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontSize: 28,
            fontWeight: 800,
            color: total > 0 ? getColor('red') : getColor('green'),
            marginBottom: 4,
          }}>
            {total}
          </div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 8 }}>
            {total > 0 ? t('needCheck', lang) : t('systemOK', lang)}
          </div>
          {critCount > 0 && (
            <div style={{
              display: 'inline-block',
              padding: '6px 10px',
              borderRadius: 6,
              background: 'var(--red-dim)',
              fontSize: 10,
              fontWeight: 600,
              color: getColor('red'),
            }}>
              {critCount} Critical
            </div>
          )}
        </div>
      );
    }

    // ── AI Health ──
    // Original: score 40px/800 colored with "%" span 14px, status text, 6px progress bar bg-elevated
    case 'ai_health': {
      const insights = getAIInsights(plant);
      const critCount = insights.warnings.filter((w) => w.sv === 'critical').length;
      const warnCount = insights.warnings.filter((w) => w.sv === 'warning').length;
      const anomCount = insights.anomalies.length;
      const score = siteScore(critCount, warnCount, anomCount);
      const hc = score >= 80 ? 'green' : score >= 60 ? 'amber' : 'red';
      return (
        <div style={{ textAlign: 'center', padding: 8 }}>
          <div style={{
            fontSize: 40,
            fontWeight: 800,
            color: getColor(hc),
            lineHeight: 1.1,
          }}>
            {score}<span style={{ fontSize: 14 }}>%</span>
          </div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
            {score >= 80 ? t('systemOK', lang) : t('needCheck', lang)}
          </div>
          <div style={{
            height: 6,
            borderRadius: 3,
            background: 'var(--bg-elevated)',
            marginTop: 10,
            overflow: 'hidden',
          }}>
            <div style={{
              width: `${score}%`,
              height: '100%',
              borderRadius: 3,
              background: getColor(hc),
              transition: 'width .3s ease',
            }} />
          </div>
        </div>
      );
    }

    // ── Water Level ──
    case 'water_level': {
      const waterDevs = plant.devices.filter((d) => d.type === 'water_level' && d.st === 'online');
      if (waterDevs.length === 0) return <div style={noDataStyle}>No water sensor online</div>;
      const wd = waterDevs[0];
      // Missing readings render as "—"; the tank graphic draws them as empty.
      const levelV = wd.data?.water_level;
      const volumeV = wd.data?.water_volume;
      const pctV = wd.data?.water_pct;
      const pct = pctV ?? 0;
      const fillHex = pctV === undefined ? '#94a3b8' : pct > 60 ? '#00E5A0' : pct > 30 ? '#FFB347' : '#FF6B6B';
      const fillColor = pctV === undefined ? 'var(--text-muted)' : pct > 60 ? 'var(--cyan)' : pct > 30 ? 'var(--amber)' : 'var(--red)';
      const fillY = 200 - (pct / 100) * 160; // SVG Y position (tank top=40, bottom=200)

      return (
        <div style={{ display: 'flex', gap: 12, alignItems: 'stretch', height: '100%', padding: 4 }}>
          {/* ── SVG Tank Graphic ── */}
          <div style={{ width: 90, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <svg viewBox="0 0 120 240" style={{ width: '100%', height: '100%', minHeight: 120 }}>
              <defs>
                <linearGradient id="waterGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={fillHex} stopOpacity="0.5" />
                  <stop offset="100%" stopColor={fillHex} stopOpacity="0.85" />
                </linearGradient>
                <clipPath id="tankClip">
                  <rect x="20" y="30" width="80" height="180" rx="12" />
                </clipPath>
              </defs>

              {/* Tank body */}
              <rect x="20" y="30" width="80" height="180" rx="12" fill="none" stroke="var(--glass-border)" strokeWidth="2" />
              {/* Tank cap */}
              <rect x="35" y="18" width="50" height="16" rx="6" fill="var(--bg-elevated)" stroke="var(--glass-border)" strokeWidth="1.5" />
              {/* Tank base */}
              <rect x="15" y="210" width="90" height="10" rx="4" fill="var(--bg-elevated)" stroke="var(--glass-border)" strokeWidth="1" />

              {/* Water fill with clip */}
              <g clipPath="url(#tankClip)">
                {/* Water body */}
                <rect x="20" y={fillY} width="80" height={200 - fillY + 10} fill="url(#waterGrad)" />

                {/* Animated wave on top */}
                <path d={`M20 ${fillY} Q35 ${fillY - 6} 50 ${fillY} Q65 ${fillY + 6} 80 ${fillY} Q95 ${fillY - 4} 100 ${fillY} V210 H20 Z`}
                  fill={fillHex} fillOpacity="0.3">
                  <animate attributeName="d"
                    values={`M20 ${fillY} Q35 ${fillY-6} 50 ${fillY} Q65 ${fillY+6} 80 ${fillY} Q95 ${fillY-4} 100 ${fillY} V210 H20 Z;M20 ${fillY} Q35 ${fillY+5} 50 ${fillY} Q65 ${fillY-5} 80 ${fillY} Q95 ${fillY+3} 100 ${fillY} V210 H20 Z;M20 ${fillY} Q35 ${fillY-6} 50 ${fillY} Q65 ${fillY+6} 80 ${fillY} Q95 ${fillY-4} 100 ${fillY} V210 H20 Z`}
                    dur="3s" repeatCount="indefinite" />
                </path>
                <path d={`M20 ${fillY+3} Q40 ${fillY+8} 60 ${fillY+2} Q80 ${fillY-4} 100 ${fillY+3} V210 H20 Z`}
                  fill={fillHex} fillOpacity="0.15">
                  <animate attributeName="d"
                    values={`M20 ${fillY+3} Q40 ${fillY+8} 60 ${fillY+2} Q80 ${fillY-4} 100 ${fillY+3} V210 H20 Z;M20 ${fillY+3} Q40 ${fillY-3} 60 ${fillY+5} Q80 ${fillY+7} 100 ${fillY+3} V210 H20 Z;M20 ${fillY+3} Q40 ${fillY+8} 60 ${fillY+2} Q80 ${fillY-4} 100 ${fillY+3} V210 H20 Z`}
                    dur="2.2s" repeatCount="indefinite" />
                </path>
              </g>

              {/* Level marks */}
              {[25, 50, 75].map((m) => {
                const my = 200 - (m / 100) * 160;
                return (
                  <g key={m}>
                    <line x1="17" y1={my} x2="22" y2={my} stroke="var(--text-muted)" strokeWidth="1" opacity="0.4" />
                    <text x="12" y={my + 3} textAnchor="end" fontSize="7" fill="var(--text-muted)" opacity="0.5">{m}%</text>
                  </g>
                );
              })}

              {/* Percentage text */}
              <text x="60" y={Math.max(fillY + 24, 70)} textAnchor="middle" fontSize="22" fontWeight="800" fill={pct > 50 ? '#fff' : fillHex}
                style={{ textShadow: pct > 50 ? '0 1px 4px rgba(0,0,0,0.4)' : 'none' }}>
                {pctV !== undefined ? `${pctV.toFixed(0)}%` : NO_VALUE}
              </text>

              {/* Device name */}
              <text x="60" y="234" textAnchor="middle" fontSize="8" fill="var(--text-muted)" fontWeight="600" letterSpacing="0.5">
                {wd.name}
              </text>
            </svg>
          </div>

          {/* ── Stats column ── */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6, justifyContent: 'center' }}>
            {/* Water Level */}
            <div style={{ padding: '10px 12px', borderRadius: 10, background: 'var(--bg-elevated)', border: '1px solid var(--glass-border)' }}>
              <div style={{ fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={fillColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2.7c0 0-8 7.3-8 13.3a8 8 0 0 0 16 0c0-6-8-13.3-8-13.3z" /></svg>
                Water Level
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: fillColor, letterSpacing: '-0.03em' }}>
                {levelV !== undefined ? levelV.toFixed(1) : NO_VALUE}<span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)', marginLeft: 3 }}>cm</span>
              </div>
            </div>

            {/* Volume */}
            <div style={{ padding: '10px 12px', borderRadius: 10, background: 'var(--bg-elevated)', border: '1px solid var(--glass-border)' }}>
              <div style={{ fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="var(--blue)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" /><path d="M4 14h16" /></svg>
                Volume
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--blue)', letterSpacing: '-0.03em' }}>
                {volumeV !== undefined ? volumeV.toLocaleString() : NO_VALUE}<span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)', marginLeft: 3 }}>L</span>
              </div>
            </div>

            {/* Fill bar */}
            <div style={{ padding: '10px 12px', borderRadius: 10, background: 'var(--bg-elevated)', border: '1px solid var(--glass-border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Fill Level</span>
                <span style={{ fontSize: 13, fontWeight: 800, color: fillColor }}>{pctV !== undefined ? `${pctV.toFixed(1)}%` : NO_VALUE}</span>
              </div>
              <div style={{ height: 8, borderRadius: 4, background: 'var(--bg-hover)', overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${Math.min(pct, 100)}%`,
                  borderRadius: 4,
                  background: `linear-gradient(90deg, ${fillColor}, ${fillColor}cc)`,
                  boxShadow: `0 0 10px ${fillColor}40`,
                  transition: 'width .6s cubic-bezier(.16,1,.3,1)',
                }} />
              </div>
            </div>
          </div>
        </div>
      );
    }

    // ── SCADA water-system schematic ──
    case 'scada':
      return /^kb-/.test(String((widget as { view3d?: string }).view3d || '')) ? <KhonBuriWaterWidget widget={widget} plant={plant} /> : <ScadaWidget widget={widget} device={findDevice(widget.deviceSN)} />;

    // ── Single water-tank SCADA (level + status zones) ──
    case 'tank_scada':
      return <TankScadaWidget widget={widget} device={findDevice(widget.deviceSN)} />;

    // ── Device connectivity monitor (online/offline + cluster correlation) ──
    case 'device_connectivity':
      return <DeviceConnectivityWidget widget={widget} plant={plant} />;

    // ── Water level (m) ↔ pump status history chart ──
    case 'level_pump':
      return <LevelPumpChart widget={widget} plant={plant} />;

    // ── Wastewater pump-station SCADA (multi-device) ──
    case 'wwtp_scada':
      { const v = (widget as { view3d?: string }).view3d; return v === 'mtech-summary' ? <MtechWwtpSummary widget={widget} plant={plant} /> : v === 'mtech' || v === 'mtech-monitor' ? <MtechWwtpWidget widget={widget} plant={plant} /> : <WwtpScadaWidget widget={widget} plant={plant} />; }

    case 'chiller_scada':
      return <ChillerScadaWidget widget={widget} plant={plant} />;
    case 'chiller_demo':
      return <ChillerDemoWidget widget={widget} plant={plant} />;
    case 'air_system_scada':
      return (/^air-/.test(String((widget as { view?: string }).view || '')) ? <AirCompressorWidget widget={widget} plant={plant} /> : <AirSystemScadaWidget widget={widget} plant={plant} />);
    case 'water_system_scada':
      return <WaterSystemScadaWidget widget={widget} plant={plant} />;
    case 'zone_energy_overview':
      return <ZoneEnergyWidget widget={widget} plant={plant} />;

    // ── Chilled-water supply/return flow (meter pair) ──
    case 'chw_flow':
      return <ChwFlowWidget widget={widget} plant={plant} />;

    case 'wq_scada':
      return <WaterQualityScadaWidget widget={widget} plant={plant} />;
    case 'mdb_scada':
      return <MdbScadaWidget widget={widget} plant={plant} />;

    case 'energy_flow_scada':
      return <EnergyFlowScadaWidget widget={widget} plant={plant} />;

    // ── Generic hub (main meter) + N branch loads energy SCADA ──
    case 'energy_hub_scada':
      return /^ev/.test(String((widget as { view?: string }).view || '')) ? <EvStationWidget widget={widget} plant={plant} /> : <EnergyHubScadaWidget widget={widget} plant={plant} />;

    case 'site_energy_map':
      return <SiteEnergyMapWidget widget={widget} plant={plant} />;

    case 'resort_ops':
      return <ResortOpsWidget widget={widget} plant={plant} />;

    case 'or_monitor':
      return <OrMonitorWidget widget={widget} plant={plant} />;

    // ── Generator: MAINS → ATS ← GEN → LOAD schematic, bound by field_no ──
    case 'genset_monitor':
      return <GensetMonitorWidget widget={widget} plant={plant} />;

    // ── Power quality: PF / THD / unbalance off a 3-phase analyser ──
    case 'power_quality':
      return <PowerQualityWidget widget={widget} device={findDevice(widget.deviceSN)} plant={plant} />;

    case 'imaging_suite_3d':
      return <ImagingSuiteWidget widget={widget} plant={plant} />;
    case 'medical_scada':
      return <MedicalScadaWidget widget={widget} plant={plant} />;
    case 'qc_monitor':
      return <QcMonitorWidget widget={widget} plant={plant} />;

    case 'grinder_scada':
      return <GrinderScadaWidget widget={widget} plant={plant} />;

    case 'extruder_scada':
      return <ExtruderScadaWidget widget={widget} plant={plant} />;

    case 'power_scada':
      return <PowerScadaWidget widget={widget} plant={plant} />;

    case 'utility_scada':
      return <UtilityScadaWidget widget={widget} plant={plant} />;

    case 'airport_stands':
      return <AirportStandsWidget widget={widget} plant={plant} />;

    case 'aq_display':
      return <AqDisplayWidget widget={widget} plant={plant} />;

    case 'facility_twin_3d':
      return <FacilityTwinWidget widget={widget} plant={plant} />;
    case 'wwtp_3d':
      return <Wwtp3DWidget widget={widget} plant={plant} />;

    case 'facility_monitor':
      return /^katathani/.test(String((widget as { view3d?: string }).view3d || '')) ? <HumidityTrialWidget widget={widget} plant={plant} /> : /^flood-/.test(String((widget as { view3d?: string }).view3d || '')) ? <FloodWidget widget={widget} plant={plant} /> : /^bkc-/.test(String((widget as { view3d?: string }).view3d || '')) ? <BkcWidget widget={widget} plant={plant} /> : /^saha-/.test(String((widget as { view3d?: string }).view3d || '')) ? <SahaFarmWidget widget={widget} plant={plant} /> : /^swine-/.test(String((widget as { view3d?: string }).view3d || '')) ? <SwineFarmWidget widget={widget} plant={plant} /> : /^school-/.test(String((widget as { view3d?: string }).view3d || '')) ? <SchoolWidget widget={widget} plant={plant} /> : /^mega-/.test(String((widget as { view3d?: string }).view3d || '')) ? <MegaMallWidget widget={widget} plant={plant} /> : /^sisb-/.test(String((widget as { view3d?: string }).view3d || '')) ? <SisbWidget widget={widget} plant={plant} /> : /^coldroom-/.test(String((widget as { view3d?: string }).view3d || '')) ? <ColdRoomWidget widget={widget} plant={plant} /> : <FacilityMonitorWidget widget={widget} plant={plant} />;

    case 'scada_tabs':
      return <ScadaTabsWidget widget={widget} plant={plant} />;

    case 'factory_scada':
      return <FactoryScadaWidget widget={widget} plant={plant} />;

    case 'food_factory_scada':
      return <FoodFactoryScadaWidget widget={widget} plant={plant} />;

    case 'boiler_scada':
      return <BoilerScadaWidget widget={widget} plant={plant} />;

    case 'steam_scada':
      return <SteamScadaWidget widget={widget} plant={plant} />;

    case 'cooling_scada':
      return <CoolingScadaWidget widget={widget} plant={plant} />;

    case 'water_scada':
      return <WaterScadaWidget widget={widget} plant={plant} />;

    case 'zones_factory_scada':
      return <ZonesFactoryScadaWidget widget={widget} plant={plant} />;

    case 'process_line_scada':
      return (/^fac-/.test(String((widget as { view?: string }).view || '')) ? <FactoryDemoWidget widget={widget} plant={plant} /> : <ProcessLineScadaWidget widget={widget} plant={plant} />);
    case 'meter_grid_scada':
      return <MeterGridScadaWidget widget={widget} plant={plant} />;
    case 'group_energy_report':
      return <GroupEnergyReportWidget widget={widget} plant={plant} />;
    case 'plant_unified_overview':
      return <PlantUnifiedWidget widget={widget} plant={plant} />;
    case 'plant_twin_3d':
      return <PlantTwinWidget widget={widget} plant={plant} />;
    case 'chiller_loop_scada':
      return <ChillerLoopScadaWidget widget={widget} plant={plant} />;

    case 'poultry_house_scada':
      return <PoultryHouseScadaWidget widget={widget} plant={plant} />;

    case 'hvac_scada':
      return <HvacScadaWidget widget={widget} plant={plant} />;

    case 'hotel_scada':
      return <HotelScadaWidget widget={widget} plant={plant} />;

    case 'mall_scada':
      return <MallScadaWidget widget={widget} plant={plant} />;

    case 'retail_portfolio':
      return <RetailPortfolioWidget widget={widget} plant={plant} />;

    case 'photo_scada':
      return <PhotoScadaWidget widget={widget} plant={plant} />;

    case 'meter_compare':
      return <MeterCompareWidget widget={widget} plant={plant} />;
    case 'solar_assess':
      return <SolarAssessWidget widget={widget} plant={plant} />;
    case 'load_profile':
      return <LoadProfileWidget widget={widget} plant={plant} />;

    case 'mdb_health':
      return <MdbHealthWidget widget={widget} plant={plant} />;

    // ── K-saver energy-saving comparison ──
    case 'ksaver':
      return <KSaverWidget widget={widget} device={findDevice(widget.deviceSN)} />;

    // ── Energy saving: baseline (manual) vs actual consumption ──
    case 'energy_saving':
      return <EnergySavingWidget widget={widget} plant={plant} />;

    // ── Solar + battery flow schematic ──
    case 'solar_flow':
      return /^pv-/.test(String((widget as { view?: string }).view || '')) ? <SolarPlantWidget widget={widget} plant={plant} /> : <SolarFlowWidget widget={widget} device={findDevice(widget.deviceSN)} />;

    // ── Solar energy balance (donuts + KPIs) ──
    case 'solar_balance':
      return <SolarBalanceWidget widget={widget} device={findDevice(widget.deviceSN)} />;

    // ── Alarm / temperature-limit setting (remote) ──
    case 'alarm_setting':
      return <AlarmSettingWidget widget={widget} device={findDevice(widget.deviceSN)} />;

    // ── Image ──
    case 'image':
      return <ImageWidget widget={widget} plant={plant} />;

    // ── Clock / Time ──
    case 'time':
      return <TimeWidget widget={widget} />;

    // ── Map (reuses the Operation Center Leaflet lib) ──
    case 'map':
      return <MapWidget widget={widget} plant={plant} />;

    // ── Default ──
    default:
      return (
        <div style={noDataStyle}>Unknown widget type</div>
      );
  }
}
